import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { RecommendationCard } from "../../components/manager/RecommendationCard";
import { Sparkline } from "../../components/manager/Sparkline";
import { StatusPill } from "../../components/manager/StatusPill";
import { useTenantAuth } from "../../context/auth";
import { apiGet } from "../../lib/api";

type DashboardKpi = {
  no_show_rate: number;
  today_appointments: number;
  pending_messages: number;
  low_stock_items: number;
  waitlist_open: number;
  waitlist_booked_7d: number;
  window_days: number;
};

type OpsSnapshot = {
  window_days: number;
  trend: Array<{
    day: string;
    total: number;
    completed: number;
    cancelled: number;
    no_show: number;
    pending_confirmation: number;
    confirmed: number;
  }>;
  today_status_counts: {
    pending_confirmation: number;
    confirmed: number;
    completed: number;
    cancelled: number;
    no_show: number;
  };
  upcoming_high_risk: Array<{
    customer_id: string;
    customer_name: string;
    risk_score: number;
    risk_tier: string;
    next_appointment_at: string;
    staff_name?: string | null;
    service_name?: string | null;
    status: string;
  }>;
  queue_health: {
    pending: number;
    failed_24h: number;
    oldest_pending_minutes: number;
  };
};

type StaffPerformance = {
  window_days: number;
  items: Array<{
    staff_id: string;
    staff_name: string;
    completed: number;
    no_show_rate: number;
    revenue_cents: number;
  }>;
};

export function ManagerDashboardPage() {
  const navigate = useNavigate();
  const { bootstrap } = useTenantAuth();
  const { t } = useTranslation();
  const tr = (key: string, fallback: string, options?: Record<string, unknown>) => {
    const translated = t(key, { ...options, defaultValue: fallback });
    return translated === key ? fallback : translated;
  };
  const [branchId, setBranchId] = useState("");

  const kpi = useQuery({
    queryKey: ["manager-dashboard-kpi"],
    queryFn: () => apiGet<DashboardKpi>("/v3/analytics/dashboard?window_days=14"),
  });

  const ops = useQuery({
    queryKey: ["manager-dashboard-ops", branchId],
    queryFn: () =>
      apiGet<OpsSnapshot>(`/v3/analytics/ops-snapshot?window_days=14${branchId ? `&branch_id=${encodeURIComponent(branchId)}` : ""}`),
  });

  const staff = useQuery({
    queryKey: ["manager-dashboard-staff", branchId],
    queryFn: () =>
      apiGet<StaffPerformance>(`/v3/analytics/staff-performance?window_days=30${branchId ? `&branch_id=${encodeURIComponent(branchId)}` : ""}`),
  });

  const topStaff = staff.data?.items?.[0];
  const trendTotals = (ops.data?.trend ?? []).map((row) => row.total);
  const trendNoShow = (ops.data?.trend ?? []).map((row) => row.no_show);
  const queuePending = ops.data?.queue_health.pending ?? 0;
  const todayRhythm = ops.data?.today_status_counts;

  const recommendations = useMemo(() => {
    const list: Array<{ title: string; body: string; actionLabel?: string; route?: string }> = [];
    if ((kpi.data?.no_show_rate ?? 0) >= 0.08) {
      list.push({
        title: tr("manager.dashboard.reco.noShow.title", "No-show trend is elevated"),
        body: tr("manager.dashboard.reco.noShow.body", "Review high-risk clients and trigger reminder templates for tomorrow appointments."),
        actionLabel: tr("manager.dashboard.reco.openClients", "Open Clients"),
        route: "/app/clients",
      });
    }
    if ((ops.data?.queue_health.failed_24h ?? 0) > 0) {
      list.push({
        title: tr("manager.dashboard.reco.messaging.title", "Messaging failures need attention"),
        body: tr(
          "manager.dashboard.reco.messaging.body",
          `${ops.data?.queue_health.failed_24h ?? 0} failed message(s) in the last 24h.`,
          { count: ops.data?.queue_health.failed_24h ?? 0 }
        ),
        actionLabel: tr("manager.dashboard.reco.openIntegrations", "Open Integrations"),
        route: "/app/integrations",
      });
    }
    if ((kpi.data?.low_stock_items ?? 0) > 0) {
      list.push({
        title: tr("manager.dashboard.reco.inventory.title", "Inventory risk detected"),
        body: tr("manager.dashboard.reco.inventory.body", `${kpi.data?.low_stock_items ?? 0} item(s) are at or below threshold.`, {
          count: kpi.data?.low_stock_items ?? 0,
        }),
        actionLabel: tr("manager.dashboard.reco.openInventory", "Open Inventory"),
        route: "/app/inventory",
      });
    }
    if ((kpi.data?.waitlist_open ?? 0) > 0) {
      list.push({
        title: tr("manager.dashboard.reco.waitlist.title", "Open waitlist demand"),
        body: tr("manager.dashboard.reco.waitlist.body", "{{count}} client(s) are waiting for slots.", {
          count: kpi.data?.waitlist_open ?? 0,
        }),
        actionLabel: tr("manager.dashboard.reco.openCalendar", "Open Calendar"),
        route: "/app/calendar",
      });
    }
    if ((ops.data?.upcoming_high_risk.length ?? 0) > 0) {
      list.push({
        title: tr("manager.dashboard.reco.upcoming.title", "Upcoming high-risk bookings"),
        body: tr(
          "manager.dashboard.reco.upcoming.body",
          `${ops.data?.upcoming_high_risk.length ?? 0} upcoming appointment(s) are in high risk tier.`,
          { count: ops.data?.upcoming_high_risk.length ?? 0 }
        ),
        actionLabel: tr("manager.dashboard.reco.openCalendar", "Open Calendar"),
        route: "/app/calendar",
      });
    }
    if (!list.length) {
      list.push({
        title: tr("manager.dashboard.reco.healthy.title", "Operations look healthy"),
        body: tr("manager.dashboard.reco.healthy.body", "Queue, booking rhythm, and risk indicators are within normal range."),
      });
    }
    return list;
  }, [
    kpi.data?.low_stock_items,
    kpi.data?.no_show_rate,
    kpi.data?.waitlist_open,
    ops.data?.queue_health.failed_24h,
    ops.data?.upcoming_high_risk.length,
    tr,
  ]);

  return (
    <div className="grid gap-4">
      <section className="manager-ribbon">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-[0.14em] text-slate-300">{tr("manager.dashboard.ribbon.overline", "Manager Command Center")}</p>
            <h1 className="text-3xl font-semibold mt-1">{tr("manager.dashboard.ribbon.title", "Welcome back")}</h1>
            <p className="text-sm text-slate-200 mt-2">
              {bootstrap?.tenant.name} ({bootstrap?.tenant.status})
            </p>
          </div>
          <div className="w-full sm:w-auto sm:min-w-[220px]">
            <label className="text-xs text-slate-300">{tr("manager.dashboard.branchScope", "Branch scope")}</label>
            <select className="input mt-1 bg-white/90" value={branchId} onChange={(e) => setBranchId(e.target.value)}>
              <option value="">{tr("manager.dashboard.allBranches", "All branches")}</option>
              {(bootstrap?.branches ?? []).map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </section>

      <section className="manager-kpi-grid">
        <article className="manager-kpi-card">
          <p className="text-xs uppercase tracking-wide text-slate-500">{tr("manager.dashboard.kpi.todayAppointments", "Today Appointments")}</p>
          <p className="text-3xl font-semibold mt-2">{String(kpi.data?.today_appointments ?? "-")}</p>
          <div className="mt-2">
            <Sparkline points={trendTotals} />
          </div>
        </article>
        <article className="manager-kpi-card">
          <p className="text-xs uppercase tracking-wide text-slate-500">{tr("manager.dashboard.kpi.noShowRate", "No-show Rate")}</p>
          <p className="text-3xl font-semibold mt-2">
            {kpi.data ? `${(kpi.data.no_show_rate * 100).toFixed(1)}%` : "-"}
          </p>
          <div className="mt-2">
            <Sparkline points={trendNoShow} color="#c2410c" />
          </div>
        </article>
        <article className="manager-kpi-card">
          <p className="text-xs uppercase tracking-wide text-slate-500">{tr("manager.dashboard.kpi.queuePending", "Queue Pending")}</p>
          <p className="text-3xl font-semibold mt-2">{queuePending || kpi.data?.pending_messages || "-"}</p>
          <div className="mt-3">
            <StatusPill
              label={
                (ops.data?.queue_health.failed_24h ?? 0) > 0
                  ? tr("manager.dashboard.queueFailed24h", `${ops.data?.queue_health.failed_24h ?? 0} failed in 24h`, { count: ops.data?.queue_health.failed_24h ?? 0 })
                  : tr("manager.dashboard.noQueueFailures24h", "No queue failures in 24h")
              }
              tone={(ops.data?.queue_health.failed_24h ?? 0) > 0 ? "queue-warning" : "queue-ok"}
            />
          </div>
        </article>
        <article className="manager-kpi-card">
          <p className="text-xs uppercase tracking-wide text-slate-500">{tr("manager.dashboard.kpi.lowStockAlerts", "Low Stock Alerts")}</p>
          <p className="text-3xl font-semibold mt-2">{String(kpi.data?.low_stock_items ?? "-")}</p>
          <p className="text-xs text-slate-500 mt-2">
            {tr("manager.dashboard.oldestPendingAge", "Oldest pending queue age: {{minutes}} min", { minutes: ops.data?.queue_health.oldest_pending_minutes ?? 0 })}
          </p>
        </article>
        <article className="manager-kpi-card">
          <p className="text-xs uppercase tracking-wide text-slate-500">{tr("manager.dashboard.kpi.waitlistOpen", "Open Waitlist")}</p>
          <p className="text-3xl font-semibold mt-2">{String(kpi.data?.waitlist_open ?? "-")}</p>
          <p className="text-xs text-slate-500 mt-2">
            {tr("manager.dashboard.waitlistBooked7d", "Booked from waitlist (7d): {{count}}", { count: kpi.data?.waitlist_booked_7d ?? 0 })}
          </p>
        </article>
      </section>

      <section className="grid xl:grid-cols-[1.2fr_1fr] gap-4">
        <article className="panel p-4">
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-lg font-semibold">{tr("manager.dashboard.todayRhythm", "Today rhythm")}</h2>
            <p className="text-xs text-slate-500">{tr("manager.dashboard.liveStatusMix", "Live booking status mix")}</p>
          </div>
          {ops.isLoading ? <p className="text-sm mt-3">{tr("manager.dashboard.loadingRhythm", "Loading rhythm...")}</p> : null}
          {ops.error ? <p className="text-sm mt-3 text-red-600">{String(ops.error)}</p> : null}
          {todayRhythm ? (
            <div className="mt-3 flex flex-wrap gap-2">
              <StatusPill label={tr("manager.dashboard.status.pending", "Pending {{count}}", { count: todayRhythm.pending_confirmation })} />
              <StatusPill label={tr("manager.dashboard.status.confirmed", "Confirmed {{count}}", { count: todayRhythm.confirmed })} />
              <StatusPill label={tr("manager.dashboard.status.completed", "Completed {{count}}", { count: todayRhythm.completed })} tone="risk-low" />
              <StatusPill label={tr("manager.dashboard.status.cancelled", "Cancelled {{count}}", { count: todayRhythm.cancelled })} tone="risk-medium" />
              <StatusPill label={tr("manager.dashboard.status.noShow", "No-show {{count}}", { count: todayRhythm.no_show })} tone="risk-high" />
            </div>
          ) : null}

          <div className="mt-4">
            <h3 className="font-semibold text-slate-800">{tr("manager.dashboard.upcomingHighRisk", "Upcoming high-risk appointments")}</h3>
            <div className="grid gap-2 mt-2">
              {(ops.data?.upcoming_high_risk ?? []).slice(0, 4).map((item) => (
                <article key={`${item.customer_id}-${item.next_appointment_at}`} className="manager-card">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-semibold">{item.customer_name}</p>
                    <StatusPill label={`${item.risk_tier} (${item.risk_score})`} tone="risk-high" />
                  </div>
                  <p className="text-sm text-slate-600 mt-1">
                    {(item.service_name || tr("manager.dashboard.defaultService", "Service"))} {tr("manager.dashboard.withStaff", "with")} {(item.staff_name || tr("manager.dashboard.defaultStaff", "Staff"))}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">{new Date(item.next_appointment_at).toLocaleString()}</p>
                </article>
              ))}
              {!ops.data?.upcoming_high_risk?.length && <p className="text-sm text-slate-500">{tr("manager.dashboard.noUpcomingHighRisk", "No upcoming high-risk appointments.")}</p>}
            </div>
          </div>
        </article>

        <article className="panel p-4">
          <h2 className="text-lg font-semibold">{tr("manager.dashboard.recommendations", "Recommendations")}</h2>
          <div className="grid gap-2 mt-3">
            {recommendations.map((item, idx) => (
              <RecommendationCard
                key={`${item.title}-${idx}`}
                title={item.title}
                body={item.body}
                actionLabel={item.actionLabel}
                onAction={item.route ? () => navigate(item.route!) : undefined}
              />
            ))}
          </div>

          <div className="mt-4 border-t border-slate-200 pt-4">
            <h3 className="font-semibold">{tr("manager.dashboard.topStaff30d", "Top staff (30d)")}</h3>
            {staff.isLoading ? <p className="text-sm mt-2">{tr("manager.dashboard.loadingStaff", "Loading staff performance...")}</p> : null}
            {topStaff ? (
              <div className="manager-card mt-2">
                <p className="font-semibold">{topStaff.staff_name}</p>
                <p className="text-sm text-slate-600 mt-1">{tr("manager.dashboard.completed", "Completed: {{count}}", { count: topStaff.completed })}</p>
                <p className="text-sm text-slate-600">{tr("manager.dashboard.noShowRate", "No-show rate: {{value}}%", { value: (topStaff.no_show_rate * 100).toFixed(1) })}</p>
                <p className="text-sm text-slate-600">{tr("manager.dashboard.revenue", "Revenue: ${{value}}", { value: (topStaff.revenue_cents / 100).toFixed(2) })}</p>
              </div>
            ) : null}
            {!staff.isLoading && !topStaff ? <p className="text-sm mt-2 text-slate-500">{tr("manager.dashboard.noStaffData", "No staff data yet.")}</p> : null}
          </div>
        </article>
      </section>
    </div>
  );
}
